import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { useShortlist } from "@/context/ShortlistContext";
import { LOGO } from "@/lib/images";

const NAV = [
  { to: "/kinderstemmen", label: "Stemmen" },
  { to: "/casting", label: "Casting" },
  { to: "/ai-voices", label: "AI-stemmen" },
  { to: "/studio", label: "Studio" },
  { to: "/work", label: "Werk" },
  { to: "/podcast", label: "Podcast" },
  { to: "/about", label: "Over ons" },
  { to: "/aanmelden", label: "Aanmelden" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { count } = useShortlist();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="relative mx-auto flex h-[112px] max-w-[1500px] items-center gap-3 px-4 sm:px-8 md:h-[128px] xl:h-[144px] 2xl:h-[152px]">
        {/* Oversized logo (has its own white rim) — overflows the bar into the section below */}
        <Link to="/" data-testid="logo" aria-label="VoiceOverKids.Agency home" className="absolute left-3 top-0 z-10 md:left-6">
          <img src={LOGO} alt="VoiceOverKids.Agency logo"
            className="h-[128px] w-[128px] object-contain md:h-[148px] md:w-[148px] xl:h-[184px] xl:w-[184px] 2xl:h-[200px] 2xl:w-[200px]" />
        </Link>

        {/* Wordmark — vertically centred on the logo, kept inside the white bar */}
        <Link to="/" className="flex translate-y-[8px] items-baseline pl-[128px] tracking-tight md:translate-y-[10px] md:pl-[144px] xl:translate-y-[20px] xl:pl-[180px] 2xl:translate-y-[24px] 2xl:pl-[196px]">
          <span className="font-quicksand text-2xl font-bold text-[#4a4848] sm:text-3xl 2xl:text-4xl">VoiceOverKids</span>
          <span className="relative top-[0.18em] ml-0.5 font-radicalls text-xl leading-none text-brand-red sm:text-2xl 2xl:text-3xl">.Agency</span>
        </Link>

        <nav className="ml-3 hidden items-center gap-3 min-[1360px]:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.replace("/", "")}`}
              className={({ isActive }) => `whitespace-nowrap text-sm font-semibold transition hover:text-brand-violet ${isActive ? "text-brand-violet" : "text-zinc-900"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 xl:gap-3">
          <Link to="/shortlist" data-testid="header-shortlist"
            className="relative flex h-11 w-11 items-center justify-center rounded-full text-zinc-800 transition hover:text-brand-violet">
            <Heart size={20} />
            {count > 0 && <span data-testid="shortlist-count" className="absolute right-0.5 top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-violet px-1 text-[11px] font-bold text-white">{count}</span>}
          </Link>
          <span className="hidden h-7 w-px bg-zinc-200 sm:block" aria-hidden="true" />
          <button onClick={() => navigate("/casting")} data-testid="header-cta"
            className="hidden flex-col items-center justify-center rounded-2xl bg-brand-violet px-4 py-2 text-sm font-bold leading-tight text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover sm:flex">
            <span>Wij casten</span>
            <span>voor jou</span>
          </button>
          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-700 min-[1360px]:hidden" aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="min-[1360px]:hidden border-t border-zinc-200 bg-white" data-testid="mobile-menu">
          <nav className="container-x flex flex-col py-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="py-3 text-base font-semibold text-zinc-800 border-b border-zinc-100 last:border-0">
                {n.label}
              </NavLink>
            ))}
            <Link to="/casting" onClick={() => setOpen(false)}
              className="mt-3 rounded-full bg-brand-violet px-5 py-3 text-center text-sm font-bold text-white">
              Wij casten voor jou
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
